/**
 * Post Card Component — unified platform feed
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  BookmarkIcon,
  TrashIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartIconSolid,
  BookmarkIcon as BookmarkIconSolid,
} from '@heroicons/react/24/solid';
import { PortraitFrame } from '../common/editorial/EditorialControls';
import UserAvatar from '../common/UserAvatar';
import CommentSection from './CommentSection';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const TYPE_LABELS = {
  TEXT: 'Text',
  IMAGE: 'Image',
  VIDEO: 'Video',
  LINK: 'Link',
  PROGRESS: 'Progress',
  INSIGHT: 'Insight',
  ADVISOR_PROMO: 'Advisor',
};

const PostCard = ({ post, user, onUpdate, onDelete }) => {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.liked_by_user || false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);
  const [bookmarked, setBookmarked] = useState(post.bookmarked_by_user || false);
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [loading, setLoading] = useState(false);
  const [sharingToCommunity, setSharingToCommunity] = useState(false);

  const handleLike = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/platform/posts/${post.post_id}/like`);
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (err) {
      console.error('Error liking post:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`/api/platform/posts/${post.post_id}`);
      onDelete(post.post_id);
    } catch (err) {
      alert('Failed to delete post');
    }
  };

  const handleShareToCommunity = async () => {
    if (sharingToCommunity) return;
    const comment = window.prompt('Add a comment (optional):');
    if (comment === null) return;

    try {
      setSharingToCommunity(true);
      await axios.post(`/api/platform/posts/${post.post_id}/share-to-community`, null, {
        params: { comment: comment || undefined },
      });
      alert('Post shared to Community!');
      navigate('/community');
    } catch (err) {
      alert('Failed to share to Community');
    } finally {
      setSharingToCommunity(false);
    }
  };

  const renderProgressCard = () => {
    if (post.post_type !== 'PROGRESS' || !post.progress_data) return null;
    const progress = post.progress_data;
    return (
      <div className="mt-4 surface-panel p-4 border-l-2 border-l-pine">
        <p className="text-body font-medium text-primary">{progress.milestone}</p>
        <p className="text-label text-secondary mt-1">{progress.summary}</p>
        {progress.entity_type && (
          <p className="text-label text-faint mt-2">
            From {progress.entity_type.replace('_', ' ')}
          </p>
        )}
      </div>
    );
  };

  const renderMedia = () => {
    if (post.post_type === 'IMAGE' && post.image_url) {
      return (
        <img
          src={post.image_url}
          alt="Post"
          className="w-full rounded-lg border border-hairline mt-4"
        />
      );
    }
    if (post.post_type === 'VIDEO' && post.video_url) {
      return (
        <div className="video-frame mt-4">
          <video src={post.video_url} controls className="w-full h-full" />
        </div>
      );
    }
    if (post.post_type === 'LINK' && post.link_preview) {
      const preview = post.link_preview;
      return (
        <a
          href={post.link_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-4 surface-panel p-4 hover:bg-pine-tint/30 transition-colors duration-300"
        >
          {preview.image && (
            <img
              src={preview.image}
              alt={preview.title}
              className="w-full h-48 object-cover rounded-lg border border-hairline mb-3"
            />
          )}
          <h4 className="text-body font-medium text-primary mb-1">{preview.title}</h4>
          {preview.description && (
            <p className="text-label text-secondary">{preview.description}</p>
          )}
        </a>
      );
    }
    return null;
  };

  const isOwner = user && user.user_id === post.author.user_id;
  const authorInitials = post.author.name?.charAt(0).toUpperCase() || 'U';

  return (
    <article className="surface-panel p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          {post.author.is_verified_advisor ? (
            <PortraitFrame
              size="xs"
              src={post.author.avatar_url}
              alt={post.author.name}
              initials={authorInitials}
            />
          ) : (
            <UserAvatar
              userId={post.author.user_id}
              src={post.author.avatar_url}
              alt={post.author.name}
              size="xs"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-body font-medium text-primary">{post.author.name}</span>
              {post.author.verified && (
                <CheckBadgeIcon className="w-4 h-4 text-pine shrink-0" />
              )}
              {post.author.is_verified_advisor && (
                <span className="badge-mark badge-mark-earned">Advisor</span>
              )}
              <span className="text-label text-faint">@{post.author.username}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-label text-faint">{TYPE_LABELS[post.post_type] || post.post_type}</span>
              <span className="text-faint">·</span>
              <span className="text-label text-faint">{formatDate(post.created_at)}</span>
            </div>
          </div>
        </div>

        {isOwner && (
          <button
            type="button"
            onClick={handleDelete}
            className="icon-btn text-danger hover:bg-bone-raised"
            aria-label="Delete post"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="mb-5">
        <p className="text-body text-primary whitespace-pre-wrap">{post.content}</p>
        {renderProgressCard()}
        {renderMedia()}
      </div>

      <div className="flex items-center gap-1 pt-4 border-t border-hairline">
        <button
          type="button"
          onClick={handleLike}
          disabled={loading}
          className={`icon-btn ${liked ? 'icon-btn-active' : ''}`}
        >
          {liked ? <HeartIconSolid className="w-5 h-5" /> : <HeartIcon className="w-5 h-5" />}
          <span className="text-label figure tabular-nums">{likesCount}</span>
        </button>

        <button
          type="button"
          onClick={() => setShowComments(!showComments)}
          className={`icon-btn ${showComments ? 'icon-btn-active' : ''}`}
        >
          <ChatBubbleLeftIcon className="w-5 h-5" />
          <span className="text-label figure tabular-nums">{commentsCount}</span>
        </button>

        <button
          type="button"
          onClick={() => setBookmarked(!bookmarked)}
          className={`icon-btn ${bookmarked ? 'icon-btn-active' : ''}`}
          aria-pressed={bookmarked}
        >
          {bookmarked ? (
            <BookmarkIconSolid className="w-5 h-5" />
          ) : (
            <BookmarkIcon className="w-5 h-5" />
          )}
        </button>

        <button
          type="button"
          onClick={handleShareToCommunity}
          disabled={sharingToCommunity}
          className="icon-btn"
          title="Share to Community"
        >
          <ArrowPathIcon className="w-5 h-5" />
          <span className="text-label">Share</span>
        </button>
      </div>

      {showComments && (
        <CommentSection
          postId={post.post_id}
          user={user}
          onCommentAdded={() => setCommentsCount((prev) => prev + 1)}
        />
      )}
    </article>
  );
};

export default PostCard;
